import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { PageHero, Reveal, SectionHeading } from '../components/Reveal';
import WhyUs from '../components/home/WhyUs';
import FinalCTA from '../components/home/FinalCTA';

const IMG =
  'https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjb2xsZWdlJTIwc3R1ZGVudHMlMjBzdHVkeWluZyUyMHRvZ2V0aGVyfGVufDB8fHx8MTc4ODEwNjk5N3ww&ixlib=rb-4.1.0&q=85';

const About = () => (
  <main data-testid="about-page">
    <SEO
      title="About CareerWithRaaj — Career Guidance + Planning + Skill Development"
      description="CareerWithRaaj is a career guidance, career planning, and skill development platform helping students, graduates, and professionals build careers with clarity."
    />
    <PageHero
      overline="About"
      title="Right Career. Right Skills. Right Direction."
      description="CareerWithRaaj is a career guidance and skill development platform that helps students and young professionals make better career decisions — at every stage."
    />

    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading
              index="01"
              overline="Our Belief"
              title="We Don't Just Help You Choose What to Study"
              description="We help you understand where it can take you — and what you need to do next. That's the difference between an education consultancy and a career platform."
            />
            <Reveal delay={0.2}>
              <blockquote className="mt-10 rounded-3xl border-l-4 border-coral bg-mist p-8" data-testid="about-positioning-quote">
                <p className="font-display text-xl font-semibold leading-relaxed text-navy md:text-2xl">
                  “Most students don't lack ambition. They lack a map. We build the map — and walk it with them.”
                </p>
              </blockquote>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-10 grid grid-cols-3 gap-4">
                {[
                  ['8th+', 'Grade onwards'],
                  ['13', 'Career categories'],
                  ['1', 'Continuous journey'],
                ].map(([num, label]) => (
                  <div key={label} className="rounded-2xl border border-line bg-white p-5 text-center shadow-card" data-testid={`about-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <p className="font-display text-3xl font-extrabold text-coral">{num}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl shadow-card-hover">
                <motion.img
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
                  src={IMG}
                  alt="Students learning and planning together"
                  className="h-[420px] w-full object-cover md:h-[520px]"
                  data-testid="about-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 rounded-2xl border border-white/60 bg-white/85 px-6 py-4 shadow-card-hover backdrop-blur-xl md:-left-8">
                <p className="text-xs font-bold uppercase tracking-wider text-coral">Discover. Decide. Develop.</p>
                <p className="mt-1 text-sm font-semibold text-navy">One platform for the whole journey</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-mist py-24 md:py-32" data-testid="founder-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative mx-auto max-w-sm">
              <div className="overflow-hidden rounded-3xl shadow-card-hover">
                <motion.img
                  initial={{ scale: 1.1 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  src="/raaj.jpg"
                  alt="Raaj — Founder & Career Guide, CareerWithRaaj"
                  className="aspect-[4/5] w-full object-cover"
                  data-testid="founder-photo"
                />
              </div>
              <div className="absolute -bottom-5 left-1/2 w-max -translate-x-1/2 rounded-2xl border border-white/60 bg-white/90 px-6 py-3 text-center shadow-card-hover backdrop-blur-xl">
                <p className="font-display text-base font-bold text-navy">Raaj</p>
                <p className="text-xs font-semibold uppercase tracking-wider text-coral">Founder & Career Guide</p>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <SectionHeading
              index="02"
              overline="The Person Behind It"
              title="Meet Your Career Guide"
              description="CareerWithRaaj was built on one frustration: talented students and professionals making life-shaping decisions with almost no real guidance."
            />
            <Reveal delay={0.2}>
              <p className="mt-8 text-base leading-relaxed text-slate-600">
                Raaj started CareerWithRaaj to change that — combining practical career guidance, honest planning, and
                skill development into one continuous journey. No generic advice, no one-size-fits-all answers: every
                conversation starts with <span className="font-semibold text-navy">your</span> interests, strengths, and
                situation, and ends with a clear next step you can act on.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <blockquote className="mt-8 rounded-3xl border-l-4 border-coral bg-white p-8 shadow-card" data-testid="founder-quote">
                <p className="font-display text-lg font-semibold leading-relaxed text-navy md:text-xl">
                  “Right career. Right skills. Right direction. Get those three right, and everything else follows.”
                </p>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <WhyUs />
    <FinalCTA />
  </main>
);

export default About;
